import { todayISO as melToday } from "@/lib/intl";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Send, Save, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function todayIso() {
  return melToday();
}

export default function ScheduleEmailSettings() {
  const [cfg, setCfg] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [apiKeyDraft, setApiKeyDraft] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [testEmailDraft, setTestEmailDraft] = useState("");

  async function load() {
    try {
      const [cfgRes, insRes] = await Promise.all([
        api.get("/schedule-email/config"),
        api.get("/schedule-email/instructors"),
      ]);
      setCfg(cfgRes.data);
      setInstructors(insRes.data || []);
    } catch (e) {
      toast.error("Failed to load email settings", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  useEffect(() => {
    load();
  }, []);

  function setField(k, v) {
    setCfg((c) => ({ ...c, [k]: v }));
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        sender_email: cfg.sender_email,
        sender_name: cfg.sender_name,
        manager_email: cfg.manager_email,
        manager_send_time: cfg.manager_send_time,
        manager_send_for: cfg.manager_send_for,
        morning_send_time: cfg.morning_send_time,
        afternoon_send_time: cfg.afternoon_send_time,
        morning_cutoff_hour: Number(cfg.morning_cutoff_hour),
        enabled: !!cfg.enabled,
      };
      if (apiKeyDraft.trim()) payload.resend_api_key = apiKeyDraft.trim();
      const r = await api.post("/schedule-email/config", payload);
      setCfg(r.data);
      setApiKeyDraft("");
      toast.success("Email settings saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function clearApiKey() {
    if (!window.confirm("Remove the saved Resend API key?")) return;
    try {
      await api.delete("/schedule-email/config/api-key");
      await load();
      toast.success("API key removed");
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function patchInstructor(cal_id, patch) {
    try {
      await api.post("/schedule-email/instructors", { calendar_id: cal_id, ...patch });
      await load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function preview(mode, cal_id, label) {
    try {
      const url = `/schedule-email/preview?date=${todayIso()}&mode=${mode}${cal_id ? `&instructor_id=${cal_id}` : ""}`;
      const r = await api.get(url, { responseType: "text", timeout: 60000 });
      setPreviewHtml(r.data);
      setPreviewTitle(label);
    } catch (e) {
      toast.error("Preview failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function sendTest(recipients) {
    if (!cfg?.resend_api_key_set) {
      toast.error("Add a Resend API key first");
      return;
    }
    setSending(true);
    try {
      const body = { date: todayIso(), recipients };
      if (testEmailDraft.trim()) body.test_to = testEmailDraft.trim();
      const r = await api.post("/schedule-email/send-now", body, { timeout: 120000 });
      const sent = r.data?.sent || [];
      const failed = r.data?.failed || [];
      if (sent.length) {
        toast.success(`Sent ${sent.length} email${sent.length === 1 ? "" : "s"}`, {
          description: sent.map((s) => s.to).join(", "),
        });
      }
      if (failed.length) {
        toast.error(`${failed.length} failed`, {
          description: failed.map((f) => `${f.recipient}: ${f.error}`).join(" · "),
        });
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  }

  if (!cfg) return null;

  return (
    <Card className="p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none" data-testid="schedule-email-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" /> Daily schedule email
          </div>
          <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-zinc-950">
            Auto-email the day's schedule
          </h2>
          <p className="mt-1.5 text-sm text-zinc-600 max-w-2xl">
            Manager gets the full sheet (revenue + everyone's bookings). Each instructor gets only
            their own rows with no revenue. Morning shifts (first appt before noon) are sent the
            previous evening; afternoon shifts the same morning.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="schedule-email-save"
        >
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>

      {/* Resend API key */}
      <div className="mb-5 p-4 rounded-md border border-zinc-200 bg-zinc-50/50">
        <Label className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
          <KeyRound className="h-3 w-3" /> Resend API key
        </Label>
        <div className="mt-1.5 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[280px]">
            <Input
              type={showKey ? "text" : "password"}
              value={apiKeyDraft}
              onChange={(e) => setApiKeyDraft(e.target.value)}
              placeholder={cfg.resend_api_key_set ? "•••••••• key on file — paste a new one to replace" : "re_..."}
              className="border-zinc-300 font-mono-num pr-10"
              data-testid="resend-api-key-input"
            />
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {cfg.resend_api_key_set && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearApiKey}
              className="border-rose-200 hover:bg-rose-50 text-rose-700"
              data-testid="resend-api-key-clear"
            >
              Remove key
            </Button>
          )}
          <span
            className={cn(
              "text-xs font-mono-num px-2 py-0.5 rounded border",
              cfg.resend_api_key_set
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-800"
            )}
          >
            {cfg.resend_api_key_set ? "key on file" : "no key set"}
          </span>
        </div>
        <p className="text-2xs text-zinc-500 mt-2">
          Get one free at <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="underline">resend.com/api-keys</a>.
          Default sender <code className="px-1 bg-zinc-100 rounded text-2xs">onboarding@resend.dev</code> works for testing —
          for production, verify your domain in Resend and set a custom sender below.
        </p>
      </div>

      {/* Sender + manager + times */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Sender email</Label>
          <Input
            value={cfg.sender_email || ""}
            onChange={(e) => setField("sender_email", e.target.value)}
            placeholder="noreply@yourbusiness.com"
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="sender-email-input"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Sender name</Label>
          <Input
            value={cfg.sender_name || ""}
            onChange={(e) => setField("sender_name", e.target.value)}
            placeholder="Daily Schedule"
            className="border-zinc-300 mt-1.5"
            data-testid="sender-name-input"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Manager email</Label>
          <Input
            value={cfg.manager_email || ""}
            onChange={(e) => setField("manager_email", e.target.value)}
            placeholder="manager@yourbusiness.com"
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="manager-email-input"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Manager send time (AU/Melbourne)</Label>
          <Input
            type="time"
            value={cfg.manager_send_time || ""}
            onChange={(e) => setField("manager_send_time", e.target.value)}
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="manager-send-time"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Manager email — schedule for</Label>
          <select
            value={cfg.manager_send_for || "today"}
            onChange={(e) => setField("manager_send_for", e.target.value)}
            data-testid="manager-send-for"
            className="mt-1.5 h-9 w-full border border-zinc-300 rounded-md px-2 text-sm bg-white"
          >
            <option value="today">Today's schedule (send same morning, e.g. 7am)</option>
            <option value="tomorrow">Tomorrow's schedule (send evening before, e.g. 7pm)</option>
          </select>
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            Morning shift email — sent previous evening at
          </Label>
          <Input
            type="time"
            value={cfg.morning_send_time || ""}
            onChange={(e) => setField("morning_send_time", e.target.value)}
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="morning-send-time"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            Afternoon shift email — sent same morning at
          </Label>
          <Input
            type="time"
            value={cfg.afternoon_send_time || ""}
            onChange={(e) => setField("afternoon_send_time", e.target.value)}
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="afternoon-send-time"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            Morning shift cutoff (first appt before)
          </Label>
          <Input
            type="number"
            min="0"
            max="23"
            value={cfg.morning_cutoff_hour ?? 12}
            onChange={(e) => setField("morning_cutoff_hour", e.target.value)}
            className="border-zinc-300 mt-1.5 font-mono-num w-32"
            data-testid="morning-cutoff-hour"
          />
          <p className="text-2xs text-zinc-500 mt-1">First appt before {cfg.morning_cutoff_hour ?? 12}:00 = morning shift</p>
        </div>
        <div className="flex items-center gap-2 sm:col-span-2 p-3 border border-zinc-200 rounded-md bg-zinc-50/50">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => setField("enabled", e.target.checked)}
            className="h-4 w-4"
            id="email-enabled"
            data-testid="email-enabled"
          />
          <Label htmlFor="email-enabled" className="text-sm text-zinc-900 font-medium cursor-pointer">
            Enable scheduled daily emails
          </Label>
          <span className="ml-auto text-2xs text-zinc-500">
            Disable to send only via the buttons below.
          </span>
        </div>
      </div>

      {/* Send test row */}
      <div className="mb-5 p-4 rounded-md border border-amber-200 bg-amber-50/40">
        <Label className="text-2xs uppercase tracking-wider text-amber-800">
          Send a test email now
        </Label>
        <p className="text-2xs text-zinc-600 mt-1 mb-2">
          Optional: route every test send to a single address (handy when your Resend
          account is in test mode and only your own email is verified).
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            value={testEmailDraft}
            onChange={(e) => setTestEmailDraft(e.target.value)}
            placeholder="(leave blank to use real recipient addresses)"
            className="border-zinc-300 flex-1 min-w-[260px] font-mono-num"
            data-testid="test-email-route"
          />
          <Button
            size="sm"
            onClick={() => sendTest(["manager"])}
            disabled={sending || !cfg.resend_api_key_set}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="send-test-manager"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            Send manager
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={() => preview("manager", null, "Manager preview")}
            data-testid="preview-manager"
          >
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            Preview manager
          </Button>
        </div>
      </div>

      {/* Per-instructor email overrides */}
      <div>
        <Label className="text-2xs uppercase tracking-wider text-zinc-500">
          Instructor emails ({instructors.length})
        </Label>
        <p className="text-2xs text-zinc-500 mt-1 mb-2">
          Auto-pulled from Acuity per calendar. Override here if needed.
          Untick "Send" to skip an instructor entirely.
        </p>
        <div className="border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-3 py-2 text-2xs font-semibold uppercase tracking-wider text-zinc-500">Instructor</th>
                <th className="text-left px-3 py-2 text-2xs font-semibold uppercase tracking-wider text-zinc-500">Email</th>
                <th className="text-left px-3 py-2 text-2xs font-semibold uppercase tracking-wider text-zinc-500 w-16">Send</th>
                <th className="text-left px-3 py-2 text-2xs font-semibold uppercase tracking-wider text-zinc-500 w-44">Test</th>
              </tr>
            </thead>
            <tbody>
              {instructors.map((it) => (
                <InstructorRow
                  key={it.calendar_id}
                  it={it}
                  onSaveEmail={(email) => patchInstructor(it.calendar_id, { email })}
                  onToggleSend={(send_enabled) => patchInstructor(it.calendar_id, { send_enabled })}
                  onPreview={() => preview("instructor", it.calendar_id, `${it.name} preview`)}
                  onSendTest={() => sendTest([`instructor:${it.calendar_id}`])}
                  apiKeySet={cfg.resend_api_key_set}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* HTML Preview dialog */}
      <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewTitle}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Live preview of the email body sent to this recipient.
            </DialogDescription>
          </DialogHeader>
          <iframe
            title="email preview"
            srcDoc={previewHtml || ""}
            className="w-full flex-1 border border-zinc-200 rounded-md bg-white"
            sandbox=""
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function InstructorRow({ it, onSaveEmail, onToggleSend, onPreview, onSendTest, apiKeySet }) {
  const [draft, setDraft] = useState(it.override_email || "");
  const effective = it.override_email || it.acuity_email || "";

  return (
    <tr className="border-b last:border-0 border-zinc-100">
      <td className="px-3 py-2">
        <div className="font-medium text-zinc-950">{it.name}</div>
        <div className="text-2xs text-zinc-400 font-mono-num">cal {it.calendar_id}</div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={it.acuity_email || "no email on Acuity calendar"}
            className="border-zinc-300 font-mono-num h-8 text-xs flex-1 min-w-[220px]"
            data-testid={`instructor-email-${it.calendar_id}`}
          />
          {(draft !== (it.override_email || "")) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSaveEmail(draft.trim())}
              className="h-8"
              data-testid={`instructor-email-save-${it.calendar_id}`}
            >
              Save
            </Button>
          )}
          {it.override_email && (
            <span className="text-2xs text-zinc-500">override</span>
          )}
        </div>
        {it.acuity_email && it.override_email && it.override_email !== it.acuity_email && (
          <div className="text-2xs text-zinc-400 mt-0.5 font-mono-num">
            Acuity has: {it.acuity_email}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={it.send_enabled !== false}
          onChange={(e) => onToggleSend(e.target.checked)}
          className="h-4 w-4"
          data-testid={`instructor-send-toggle-${it.calendar_id}`}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={onPreview}
            className="h-7 text-xs"
            data-testid={`instructor-preview-${it.calendar_id}`}
          >
            Preview
          </Button>
          <Button
            size="sm"
            onClick={onSendTest}
            disabled={!apiKeySet || !effective}
            className="h-7 text-xs bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid={`instructor-send-${it.calendar_id}`}
          >
            Send
          </Button>
        </div>
      </td>
    </tr>
  );
}
